let roleFilter: "all" | "parent" | "pro" = "all";
let cityFilter = "all";
const knownCities = new Map<string, string>();
let observer: MutationObserver | null = null;
let scheduled = false;

const normalize = (value: string) => value.trim().toLocaleLowerCase("fr-FR");

function findUsersArea() {
  const searchInput = document.querySelector<HTMLInputElement>('input[placeholder^="Rechercher par nom, email, ville"]');
  if (!searchInput) return null;

  let root: HTMLElement | null = searchInput.parentElement;
  while (root && !root.querySelector("table")) root = root.parentElement;
  if (!root) return null;

  const table = root.querySelector<HTMLTableElement>("table");
  const searchWrap = searchInput.parentElement as HTMLElement | null;
  if (!table || !searchWrap) return null;
  return { root, table, searchWrap };
}

function collectCities(table: HTMLTableElement) {
  table.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach((row) => {
    const cells = row.querySelectorAll<HTMLTableCellElement>("td");
    if (cells.length < 4) return;
    const city = cells[3].textContent?.trim() || "";
    if (!city || city === "—") return;
    const key = normalize(city);
    if (!knownCities.has(key)) knownCities.set(key, city);
  });
}

function updateCitySelect(select: HTMLSelectElement) {
  const current = cityFilter;
  const values = Array.from(knownCities.entries()).sort((a, b) => a[1].localeCompare(b[1], "fr", { sensitivity: "base" }));
  const signature = values.map(([key]) => key).join("|");
  if (select.dataset.signature === signature) return;
  select.dataset.signature = signature;
  select.innerHTML = '<option value="all">Toutes les villes</option>' + values.map(([key, label]) => `<option value="${key.replace(/"/g, "&quot;")}">${label}</option>`).join("");
  select.value = values.some(([key]) => key === current) ? current : "all";
  if (select.value === "all" && current !== "all") cityFilter = "all";
}

function applyFilters(table: HTMLTableElement, status: HTMLElement) {
  let visible = 0;
  let total = 0;
  table.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach((row) => {
    const cells = row.querySelectorAll<HTMLTableCellElement>("td");
    if (cells.length < 4) return;
    total++;
    const role = normalize(cells[2].textContent || "");
    const city = normalize(cells[3].textContent || "");
    const roleOk = roleFilter === "all" || (roleFilter === "parent" ? role.includes("parent") : role.includes("pro"));
    const cityOk = cityFilter === "all" || city === cityFilter;
    const show = roleOk && cityOk;
    row.style.display = show ? "" : "none";
    if (show) visible++;
  });

  const active = roleFilter !== "all" || cityFilter !== "all";
  status.textContent = active ? `${visible} résultat${visible > 1 ? "s" : ""} filtré${visible > 1 ? "s" : ""} sur ${total} affiché${total > 1 ? "s" : ""}` : `${total} utilisateur${total > 1 ? "s" : ""} affiché${total > 1 ? "s" : ""}`;
}

function createControls(searchWrap: HTMLElement, table: HTMLTableElement) {
  const existing = document.getElementById("cg-admin-user-filters");
  if (existing) return existing;

  const controls = document.createElement("div");
  controls.id = "cg-admin-user-filters";
  controls.className = "mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3";
  controls.innerHTML = `
    <div class="flex items-center gap-1 rounded-xl border bg-background p-1" data-role-group>
      <button type="button" data-role="all" class="h-8 px-3 rounded-lg text-xs font-medium transition-colors bg-primary text-primary-foreground">Tous</button>
      <button type="button" data-role="parent" class="h-8 px-3 rounded-lg text-xs font-medium transition-colors hover:bg-muted">Parents</button>
      <button type="button" data-role="pro" class="h-8 px-3 rounded-lg text-xs font-medium transition-colors hover:bg-muted">Pros</button>
    </div>
    <select data-city-filter class="h-10 min-w-[180px] rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
      <option value="all">Toutes les villes</option>
    </select>
    <span data-filter-status class="text-xs text-muted-foreground sm:ml-auto"></span>
  `;
  searchWrap.insertAdjacentElement("afterend", controls);

  const roleButtons = controls.querySelectorAll<HTMLButtonElement>("button[data-role]");
  const citySelect = controls.querySelector<HTMLSelectElement>("select[data-city-filter]")!;
  const status = controls.querySelector<HTMLElement>("[data-filter-status]")!;

  const refreshRoleButtons = () => {
    roleButtons.forEach((button) => {
      const active = button.dataset.role === roleFilter;
      button.classList.toggle("bg-primary", active);
      button.classList.toggle("text-primary-foreground", active);
      button.classList.toggle("hover:bg-muted", !active);
    });
  };

  roleButtons.forEach((button) => {
    button.addEventListener("click", () => {
      roleFilter = (button.dataset.role as typeof roleFilter) || "all";
      refreshRoleButtons();
      applyFilters(table, status);
    });
  });

  citySelect.addEventListener("change", () => {
    cityFilter = citySelect.value;
    applyFilters(table, status);
  });

  collectCities(table);
  updateCitySelect(citySelect);
  applyFilters(table, status);
  return controls;
}

function refresh() {
  scheduled = false;
  if (window.location.pathname !== "/admin") {
    document.getElementById("cg-admin-user-filters")?.remove();
    return;
  }

  const area = findUsersArea();
  if (!area) return;
  const controls = createControls(area.searchWrap, area.table);
  collectCities(area.table);
  const select = controls.querySelector<HTMLSelectElement>("select[data-city-filter]");
  const status = controls.querySelector<HTMLElement>("[data-filter-status]");
  if (select) updateCitySelect(select);
  if (status) applyFilters(area.table, status);
}

function scheduleRefresh() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(refresh);
}

export function registerAdminUserFilters() {
  if (typeof window === "undefined" || observer) return;
  const start = () => {
    scheduleRefresh();
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("popstate", scheduleRefresh);
    window.addEventListener("hashchange", scheduleRefresh);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
